<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    header="Таможенный документ"
    :modal="true"
    :style="{ width: '540px' }"
    :closable="true"
  >
    <div class="mb-4">
      <p class="text-600 mb-3">
        Загрузите файл заказа из PartsSoft (XLS/XLSX). Система обогатит данные
        из базы товаров, вычислит HS-коды и сформирует готовый документ.
      </p>

      <FileUpload
        ref="fileUpload"
        mode="basic"
        :auto="false"
        accept=".xls,.xlsx"
        :maxFileSize="104857600"
        chooseLabel="Выбрать файл"
        class="w-full"
        @select="onFileSelect"
      />

      <div v-if="selectedFile" class="mt-2 text-sm text-700">
        <i class="pi pi-file mr-1"></i>
        {{ selectedFile.name }}
      </div>

      <!-- Настройка оптимизации налогов -->
      <div class="mt-4 p-3 border-1 border-round surface-border">
        <div class="flex align-items-center justify-content-between mb-2">
          <label class="font-semibold text-sm">Оптимизация налогов</label>
          <span class="text-primary font-bold text-lg">{{ savingsPct }}%</span>
        </div>

        <Slider v-model="savingsPct" :min="0" :max="30" :step="0.5" class="mb-2" />

        <div class="flex justify-content-between text-xs text-500">
          <span>0% — без оптимизации</span>
          <span>30% — максимум</span>
        </div>

        <p class="text-xs text-500 mt-2 mb-0 line-height-3">
          Определяет целевую экономию налогов. Система перераспределяет стоимость
          между товарами 9004 (пошлина 5% + НДС 22%) и 9003 (пошлина 12.5%, без НДС),
          снижая цены товаров с высоким налогом и повышая с низким.
          Общая сумма инвойса не меняется. Рекомендуемое значение: 5-6%.
        </p>
      </div>

      <div v-if="notFoundCount > 0" class="mt-3 p-message p-message-warn">
        <div class="p-message-wrapper">
          <span class="p-message-icon pi pi-exclamation-triangle"></span>
          <div class="p-message-text">
            {{ notFoundCount }} товар(ов) не найдено в базе данных
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        icon="pi pi-times"
        class="p-button-text"
        @click="$emit('update:visible', false)"
      />
      <Button
        label="Сгенерировать"
        icon="pi pi-file-export"
        class="p-button-warning"
        :loading="generating"
        :disabled="!selectedFile"
        @click="generate"
      />
    </template>
  </Dialog>
</template>

<script>
import { ref, watch, toRef } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import FileUpload from 'primevue/fileupload'
import Slider from 'primevue/slider'
import { useToast } from 'primevue/usetoast'
import apiService from '../services/apiService'

export default {
  name: 'ShipmentExportDialog',
  components: { Dialog, Button, FileUpload, Slider },
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ['update:visible'],
  setup(props, { emit }) {
    const toast = useToast()
    const fileUpload = ref(null)
    const selectedFile = ref(null)
    const generating = ref(false)
    const notFoundCount = ref(0)
    const savingsPct = ref(5.5)

    // Сброс состояния при закрытии диалога
    watch(toRef(props, 'visible'), (val) => {
      if (!val) {
        selectedFile.value = null
        notFoundCount.value = 0
      }
    })

    const onFileSelect = (event) => {
      selectedFile.value = event.files?.[0] || null
    }

    const generate = async () => {
      if (!selectedFile.value) return

      generating.value = true
      notFoundCount.value = 0

      try {
        const response = await apiService.generateShipmentExport(
          selectedFile.value,
          savingsPct.value
        )

        // Проверяем заголовок X-Not-Found-Count
        const nfc = response.headers?.['x-not-found-count']
        if (nfc) {
          notFoundCount.value = parseInt(nfc, 10)
        }

        // Скачиваем blob
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        // Извлекаем имя файла из Content-Disposition
        const disposition = response.headers?.['content-disposition']
        let filename = 'Shipment_Details.xlsx'
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/)
          if (match) filename = match[1]
        }

        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        const detail = notFoundCount.value > 0
          ? `Файл сформирован. ${notFoundCount.value} товар(ов) не найдено в БД.`
          : 'Файл успешно сформирован и загружен'

        toast.add({
          severity: notFoundCount.value > 0 ? 'warn' : 'success',
          summary: 'Таможенный документ',
          detail,
          life: 5000
        })

        if (notFoundCount.value === 0) {
          emit('update:visible', false)
          selectedFile.value = null
        }
      } catch (error) {
        console.error('Shipment export error:', error)

        let detail = 'Не удалось сгенерировать документ'
        if (error.response?.data) {
          // Blob error — нужно прочитать как текст
          try {
            const text = await error.response.data.text()
            const json = JSON.parse(text)
            detail = json.detail || detail
          } catch {
            // ignore parse errors
          }
        }

        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail,
          life: 5000
        })
      } finally {
        generating.value = false
      }
    }

    return {
      fileUpload,
      selectedFile,
      generating,
      notFoundCount,
      savingsPct,
      onFileSelect,
      generate
    }
  }
}
</script>
